import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { officeAdminAPI } from '../../services/api';

const ROLE_LABEL = { admin: 'مدير مكتب', supervisor: 'مشرف' };

export default function OfficesPage() {
  const [offices, setOffices]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filter, setFilter]         = useState('');

  // Create office modal
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [form, setForm]             = useState({ name: '', city: '', address: '', phone: '', email: '', registrationNumber: '', notes: '' });

  // Detail drawer
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [officeCustomers, setOfficeCustomers] = useState([]);

  // Edit office modal — corrects data-entry mistakes (e.g. a mistyped city)
  // without touching approval/commission state.
  const [showEdit, setShowEdit]     = useState(false);
  const [editForm, setEditForm]     = useState({ name: '', city: '', address: '', phone: '', email: '', registrationNumber: '', notes: '' });
  const [editing, setEditing]       = useState(false);

  // Reassign customer's first office
  const [reassignCustomer, setReassignCustomer] = useState(null);
  const [reassignTarget, setReassignTarget]     = useState('');
  const [reassigning, setReassigning]           = useState(false);

  // Add employee modal
  const [showEmp, setShowEmp]       = useState(false);
  const [empForm, setEmpForm]       = useState({ name: '', email: '', phone: '', password: '', role: 'supervisor' });
  const [empSaving, setEmpSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      if (search) params.search = search;
      const res = await officeAdminAPI.list(params);
      setOffices(res.data.data || []);
    } catch { toast.error('خطأ في تحميل المكاتب'); }
    finally { setLoading(false); }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const [officeRes, customersRes] = await Promise.all([
        officeAdminAPI.get(id),
        officeAdminAPI.listCustomers(id).catch(() => ({ data: { data: [] } })),
      ]);
      setDetail(officeRes.data.data);
      setOfficeCustomers(customersRes.data.data || []);
    } catch { toast.error('خطأ في تحميل بيانات المكتب'); }
    finally { setDetailLoading(false); }
  };

  const handleReassign = async () => {
    if (!reassignTarget) return toast.error('اختر المكتب الجديد');
    setReassigning(true);
    try {
      await officeAdminAPI.reassignCustomer(reassignCustomer.id, reassignTarget);
      toast.success('تم نقل العميل إلى المكتب الجديد');
      setReassignCustomer(null);
      setReassignTarget('');
      openDetail(detail.id);
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ في النقل'); }
    finally { setReassigning(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error('الاسم والهاتف مطلوبان');
    setCreating(true);
    try {
      await officeAdminAPI.create(form);
      toast.success('تم إنشاء المكتب. يمكنك تفعيله الآن.');
      setShowCreate(false);
      setForm({ name: '', city: '', address: '', phone: '', email: '', registrationNumber: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ في الإنشاء'); }
    finally { setCreating(false); }
  };

  const openEdit = () => {
    setEditForm({
      name: detail.name || '', city: detail.city || '', address: detail.address || '',
      phone: detail.phone || '', email: detail.email || '',
      registrationNumber: detail.registrationNumber || '', notes: detail.notes || '',
    });
    setShowEdit(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.name || !editForm.phone) return toast.error('الاسم والهاتف مطلوبان');
    setEditing(true);
    try {
      await officeAdminAPI.update(detail.id, editForm);
      toast.success('تم تحديث بيانات المكتب');
      setShowEdit(false);
      openDetail(detail.id);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ في التحديث'); }
    finally { setEditing(false); }
  };

  const handleApprove = async (id) => {
    try {
      await officeAdminAPI.approve(id);
      toast.success('تم تفعيل المكتب');
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ'); }
  };

  const handleSuspend = async (id) => {
    if (!window.confirm('هل أنت متأكد من إيقاف هذا المكتب؟')) return;
    try {
      await officeAdminAPI.suspend(id);
      toast.success('تم إيقاف المكتب');
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) { toast.error(e.response?.data?.message || 'خطأ'); }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setEmpSaving(true);
    try {
      await officeAdminAPI.addEmployee(detail.id, empForm);
      toast.success('تم إضافة الموظف بنجاح');
      setShowEmp(false);
      setEmpForm({ name: '', email: '', phone: '', password: '', role: 'supervisor' });
      openDetail(detail.id);
    } catch (err) { toast.error(err.response?.data?.message || 'خطأ في الإضافة'); }
    finally { setEmpSaving(false); }
  };

  const handleToggleEmployee = async (empId) => {
    try {
      await officeAdminAPI.toggleEmployee(detail.id, empId);
      toast.success('تم تغيير حالة الموظف');
      openDetail(detail.id);
    } catch { toast.error('خطأ'); }
  };

  return (
    <div dir="rtl" className="p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">مكاتب الحجز</h1>
          <p className="text-sm text-gray-500 mt-1">إدارة مكاتب الحجز الشريكة للمنصة</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition">
          + إضافة مكتب
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <input
          className="border rounded-lg px-3 py-2 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="بحث باسم المكتب..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="border rounded-lg px-3 py-2 text-sm focus:outline-none" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">الكل</option>
          <option value="active">نشط</option>
          <option value="inactive">موقوف / معلق</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">جاري التحميل...</div>
      ) : offices.length === 0 ? (
        <div className="text-center py-16 text-gray-400">لا توجد مكاتب</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-right">المكتب</th>
                <th className="px-4 py-3 text-right">المدينة</th>
                <th className="px-4 py-3 text-right">الهاتف</th>
                <th className="px-4 py-3 text-right">الحجوزات</th>
                <th className="px-4 py-3 text-right">الحالة</th>
                <th className="px-4 py-3 text-right">إجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {offices.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-900">{o.name}</td>
                  <td className="px-4 py-3 text-gray-600">{o.city || '—'}</td>
                  <td className="px-4 py-3 text-gray-600" dir="ltr">{o.phone}</td>
                  <td className="px-4 py-3 text-gray-700">{o.bookingCount ?? 0}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${o.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {o.isActive ? 'نشط' : 'معلق'}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <button onClick={() => openDetail(o.id)} className="text-blue-600 hover:underline text-xs font-semibold">تفاصيل</button>
                    {!o.isActive
                      ? <button onClick={() => handleApprove(o.id)} className="text-green-600 hover:underline text-xs font-semibold">تفعيل</button>
                      : <button onClick={() => handleSuspend(o.id)} className="text-red-500 hover:underline text-xs font-semibold">إيقاف</button>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Office Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
            <h2 className="text-lg font-bold mb-5">إضافة مكتب حجز جديد</h2>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">اسم المكتب *</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">المدينة</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">الهاتف *</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" dir="ltr" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">البريد الإلكتروني</label>
                  <input type="email" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" dir="ltr" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">رقم السجل التجاري</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={form.registrationNumber} onChange={(e) => setForm({...form, registrationNumber: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">العنوان</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">ملاحظات داخلية</label>
                <textarea rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={creating} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                  {creating ? 'جاري الإنشاء...' : 'إنشاء المكتب'}
                </button>
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border py-2 rounded-lg font-semibold text-sm text-gray-600 hover:bg-gray-50 transition">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Office Modal — opened from within the Detail Drawer (also z-50),
          so this needs a higher stacking context to render on top of it rather
          than being hidden underneath. */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
            <h2 className="text-lg font-bold mb-5">تعديل بيانات المكتب</h2>
            <form onSubmit={handleEditSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">اسم المكتب *</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} required />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">المدينة</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">الهاتف *</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" dir="ltr" value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})} required />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">البريد الإلكتروني</label>
                  <input type="email" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" dir="ltr" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">رقم السجل التجاري</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={editForm.registrationNumber} onChange={(e) => setEditForm({...editForm, registrationNumber: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">العنوان</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">ملاحظات داخلية</label>
                <textarea rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={editing} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                  {editing ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                </button>
                <button type="button" onClick={() => setShowEdit(false)} className="flex-1 border py-2 rounded-lg font-semibold text-sm text-gray-600 hover:bg-gray-50 transition">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-end lg:items-center justify-center z-50 p-0 lg:p-4">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {detailLoading ? (
              <div className="p-10 text-center text-gray-400">جاري التحميل...</div>
            ) : (
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{detail.name}</h2>
                    <p className="text-sm text-gray-500">{detail.city} {detail.address ? `· ${detail.address}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${detail.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {detail.isActive ? 'نشط' : 'معلق'}
                    </span>
                    <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{detail.bookingCount ?? 0}</div>
                    <div className="text-xs text-blue-500 mt-1">إجمالي الحجوزات</div>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-700">{(detail.commissionTotal ?? 0).toLocaleString()}</div>
                    <div className="text-xs text-emerald-500 mt-1">عمولة مستحقة (ج.س)</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className="text-2xl font-bold text-gray-700">{(detail.employees || []).length}</div>
                    <div className="text-xs text-gray-400 mt-1">الموظفون</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mb-6">
                  {!detail.isActive
                    ? <button onClick={() => handleApprove(detail.id)} className="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition">تفعيل المكتب</button>
                    : <button onClick={() => handleSuspend(detail.id)} className="flex-1 bg-red-500 text-white py-2 rounded-lg text-sm font-semibold hover:bg-red-600 transition">إيقاف المكتب</button>
                  }
                  <button onClick={() => setShowEmp(true)} className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition">+ إضافة موظف</button>
                  <button onClick={openEdit} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition">✎ تعديل البيانات</button>
                </div>

                {/* Commission profile badge */}
                {detail.commissionProfile && (
                  <div className="mb-5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5">
                    <p className="text-xs text-emerald-600 font-medium mb-0.5">ملف تعريف العمولة المعين</p>
                    <p className="font-bold text-emerald-700">{detail.commissionProfile.name}</p>
                  </div>
                )}

                {/* Employees list */}
                <h3 className="font-bold text-gray-700 mb-3 text-sm">الموظفون</h3>
                {(detail.employees || []).length === 0 ? (
                  <p className="text-sm text-gray-400">لا يوجد موظفون مضافون بعد</p>
                ) : (
                  <div className="space-y-2">
                    {detail.employees.map((emp) => (
                      <div key={emp.id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                        <div>
                          <p className="font-semibold text-sm text-gray-800">{emp.user?.name}</p>
                          <p className="text-xs text-gray-500">{emp.user?.email} · {ROLE_LABEL[emp.role]}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${emp.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                            {emp.isActive ? 'نشط' : 'موقوف'}
                          </span>
                          <button onClick={() => handleToggleEmployee(emp.id)} className="text-xs text-gray-500 hover:text-gray-800 underline">
                            {emp.isActive ? 'إيقاف' : 'تفعيل'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Acquired customers — customers whose firstBookingOfficeId is this office */}
                <h3 className="font-bold text-gray-700 mb-3 mt-6 text-sm">العملاء المكتسبون</h3>
                {officeCustomers.length === 0 ? (
                  <p className="text-sm text-gray-400">لا يوجد عملاء مكتسبون بعد</p>
                ) : (
                  <div className="space-y-2">
                    {officeCustomers.map((c) => (
                      <div key={c.id} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                        <div>
                          <p className="font-semibold text-sm text-gray-800">{c.fullName || 'بدون اسم'}</p>
                          <p className="text-xs text-gray-500" dir="ltr">{c.mobileNumber}</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {c.totalOfficeBookings ?? 0} حجز مكتب · {c.totalDirectBookings ?? 0} حجز مباشر
                          </p>
                        </div>
                        <button
                          onClick={() => { setReassignCustomer(c); setReassignTarget(''); }}
                          className="text-xs text-blue-600 hover:underline font-semibold whitespace-nowrap"
                        >
                          نقل إلى مكتب آخر
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reassign Customer Modal */}
      {reassignCustomer && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <h2 className="text-lg font-bold mb-2">نقل العميل إلى مكتب آخر</h2>
            <p className="text-sm text-gray-500 mb-5">{reassignCustomer.fullName || reassignCustomer.mobileNumber}</p>
            <label className="block text-xs text-gray-600 mb-1">المكتب الجديد</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-5"
              value={reassignTarget}
              onChange={(e) => setReassignTarget(e.target.value)}
            >
              <option value="">— اختر مكتباً —</option>
              {offices.filter((o) => o.id !== detail?.id).map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button
                onClick={handleReassign}
                disabled={reassigning}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {reassigning ? 'جاري النقل...' : 'تأكيد النقل'}
              </button>
              <button
                type="button"
                onClick={() => setReassignCustomer(null)}
                className="flex-1 border py-2 rounded-lg font-semibold text-sm text-gray-600 hover:bg-gray-50 transition"
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showEmp && detail && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <h2 className="text-lg font-bold mb-5">إضافة موظف — {detail.name}</h2>
            <form onSubmit={handleAddEmployee} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">الاسم الكامل *</label>
                <input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={empForm.name} onChange={(e) => setEmpForm({...empForm, name: e.target.value})} required />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">البريد الإلكتروني *</label>
                <input type="email" dir="ltr" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={empForm.email} onChange={(e) => setEmpForm({...empForm, email: e.target.value})} required />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">رقم الهاتف *</label>
                <input dir="ltr" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={empForm.phone} onChange={(e) => setEmpForm({...empForm, phone: e.target.value})} required />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">كلمة المرور *</label>
                <input type="password" dir="ltr" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" value={empForm.password} onChange={(e) => setEmpForm({...empForm, password: e.target.value})} required />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">الدور</label>
                <select className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none" value={empForm.role} onChange={(e) => setEmpForm({...empForm, role: e.target.value})}>
                  <option value="supervisor">مشرف</option>
                  <option value="admin">مدير مكتب</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={empSaving} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                  {empSaving ? 'جاري الإضافة...' : 'إضافة الموظف'}
                </button>
                <button type="button" onClick={() => setShowEmp(false)} className="flex-1 border py-2 rounded-lg font-semibold text-sm text-gray-600 hover:bg-gray-50 transition">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
